/**
 * The visit page's boxes — one shell for every sheet and popup the page opens
 * (lead decision, round 2): `docs/visit-page.md` §15.1, §16.1, §19.2, §27.4;
 * Boards 7 B, 11, 12, 16 D, 17 F and 18.
 *
 * The boards draw three shapes, measured from their HTML:
 *
 * - **`VisitPanel`** — a day, opened beside the page (Board 11 A): flush to
 *   the right edge, the window's full height, 540 wide, over a 30 % scrim; a
 *   16/20 header with the day's mark and a 32px round ✕; the footer on
 *   `surface-alt`, laid out by its caller.
 * - **`VisitCard`** — a sheet as tall as what it holds (Board 12 A 602, B 472;
 *   Board 18 D 562): at the right edge, rounded 14, a 36px tile beside a
 *   16/700 title, a bare 17px ✕, a 14/20 footer carrying a sentence on its
 *   left and 36px buttons on its right. Its crops are content-tall (12 B is
 *   534 high in a 900 window), so it is never stretched to the window.
 * - **`VisitPopup`** — a question asked over the page (Board 11 B, C 470;
 *   17 F 470; 18 E 520; 18 H 420; 7 B 440): in the middle, rounded 14, a 34px
 *   tile beside a 14.5/700 title, a 30px round ✕, a 12/16 footer with 34px
 *   buttons.
 *
 * **On a phone every one of them is Board 16 D's sheet from the bottom** — 20px
 * top corners, a 40 × 4 handle, the title at 15.5/700 over its 12px line, a
 * 32px ringed ✕, one row of 48px buttons sharing the width (§19.2: *a day, a
 * receipt, a swap, an order and Review & close open as sheets from the bottom
 * with one big primary button*). The body is the same component either way;
 * only the box changes — the argument `SheetOrDialog` makes, which this does
 * not reuse because neither of its boxes is drawn here: its `Modal` is 16px
 * round with a 16/18 header, and its `PanelSheet` is the list engine's 18px,
 * 17/700, tinted-✕ sheet (Board 6 of folder 38), not Board 16 D's. What this
 * does share with them is the layer convention — every scrim carries
 * `data-overlay-layer`, and `useDismiss` decides which box an Escape or an
 * outside click belongs to.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useDismiss } from '../components/overlays.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { COMMON } from './copy.js'

/** A sheet or popup opened from the page. */
export const VISIT_LAYER = 50
/** One opened from a sheet — *Move this day* over the day sheet. */
export const VISIT_LAYER_OVER = 60

// ---------------------------------------------------------------------------
// The header's tile and the boxes' buttons
// ---------------------------------------------------------------------------

/** The tinted tile at the left of a card's or a popup's title. */
export type VisitTileTone = 'accent' | 'amber' | 'teal' | 'grey' | 'danger' | 'indigo'

const TILE_TONES: Record<VisitTileTone, string> = {
  accent: 'border-accent-line bg-accent-soft text-accent-dark',
  amber: 'border-amber-band-line bg-amber-band-bg text-amber-band-text',
  teal: 'border-teal-band-line bg-teal-band-bg text-teal-band-text',
  grey: 'border-line bg-line-soft text-text-muted',
  danger: 'border-danger-line bg-danger-bg text-danger',
  indigo: 'border-indigo-line',
}

/**
 * Board 18 H's equipment tile (`#e9ecf7` / `#5b6fb5`) is the equipment kind's
 * own pair — the status token `kind.equipment` the Equipment tab's kind tiles
 * read — so it follows that pair into dark mode too.
 */
const TILE_TINTS: Partial<Record<VisitTileTone, CSSProperties>> = {
  indigo: { color: 'rgb(var(--status-kind-equipment-fg))', background: 'rgb(var(--status-kind-equipment-bg))' },
}

/**
 * 34 on a popup (glyph 17), 36 on a card (glyph 18) — Boards 11 B, 12, 17 F,
 * 18 D, 18 H. The boards size the tile's box and draw its 1px border outside
 * it, so it stands 36 and 38 on the page; so does this.
 */
export function VisitTile({ glyph, tone, size }: { glyph: VisitGlyphName; tone: VisitTileTone; size: 34 | 36 }) {
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center rounded-[10px] border', TILE_TONES[tone])}
      style={{ width: size + 2, height: size + 2, ...TILE_TINTS[tone] }}
    >
      <VisitGlyph name={glyph} size={size === 34 ? 17 : 18} stroke={1.9} />
    </span>
  )
}

/**
 * The boxes' buttons: `popup` 34 / 12.5 (Board 11 B, C, the day sheet's
 * footer, 17 F), `card` 36 / 13 (Board 12 A, 18 D), `row` 28 / 12 (Board 12
 * B's **Review**, 18 F's **Close out**), `phone` 48 / 14.5, radius 12, each
 * sharing the row (Board 16 D).
 *
 * A primary that cannot be pressed yet is drawn as Board 12 B draws **Close
 * the visit** — grey, faint words, its border kept. A danger button (17 F's
 * **Cancel the visit**) is red words on white.
 */
export type VisitButtonSize = 'popup' | 'card' | 'row' | 'phone'
export type VisitButtonTone = 'plain' | 'primary' | 'danger'

const BUTTON_SIZES: Record<VisitButtonSize, string> = {
  popup: 'h-[34px] rounded-button px-3.5 text-[12.5px]',
  card: 'h-9 rounded-button px-3.5 text-[13px]',
  row: 'h-7 rounded-button px-3.5 text-[12px]',
  phone: 'h-12 min-w-0 flex-1 rounded-[12px] px-3.5 text-[14.5px]',
}

const BUTTON_TONES: Record<VisitButtonTone, string> = {
  plain: 'border-line bg-surface text-text hover:bg-hover',
  primary: 'border-accent bg-accent text-white hover:bg-accent-dark',
  danger: 'border-danger-line bg-surface text-danger hover:bg-danger-bg disabled:opacity-50',
}

const GLYPH_SIZES: Record<VisitButtonSize, number> = { popup: 14.5, card: 15, row: 13, phone: 16.5 }

export function VisitButton({
  size, tone = 'plain', glyph, disabled = false, onClick, children, className, label,
}: {
  size: VisitButtonSize
  tone?: VisitButtonTone
  glyph?: VisitGlyphName
  disabled?: boolean
  onClick?: () => void
  children: ReactNode
  className?: string
  /** For a button whose words are not its whole name. */
  label?: string
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className={cn(
        // A button does not inherit the page's figures, and the boards' do (*Close out Day 1*).
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap border font-semibold tabular-nums',
        BUTTON_SIZES[size],
        disabled && tone !== 'danger'
          ? 'cursor-not-allowed border-line bg-line-soft text-text-faint'
          : BUTTON_TONES[tone],
        disabled && 'cursor-not-allowed',
        className,
      )}
    >
      {glyph && <VisitGlyph name={glyph} size={GLYPH_SIZES[size]} stroke={2} />}
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// The boxes
// ---------------------------------------------------------------------------

type Shape = 'panel' | 'card' | 'popup'

export interface VisitBoxProps {
  /** The box's width as its board draws it, border included (540, 602, 472, 562, 470, 520, 440, 420). */
  width: number
  /** Board 16 D's sheet from the bottom. Left out, the window decides (`usePhone`). */
  phone?: boolean
  title: string
  sub?: string
  /** The desktop header's mark — a `VisitTile`, or a day's visit mark at 44. A phone's sheet draws none. */
  mark?: ReactNode
  onClose: () => void
  /**
   * The footer's buttons. On a card and a popup they sit on the right, after
   * `note`; a panel lays its footer out itself. On a phone pass them sized
   * `phone`, so they share the row.
   */
  footer?: ReactNode
  /** The footer's sentence (*The visit closes once every line passes.*); a phone's sheet puts it under the body. */
  note?: string
  /** The sentence's size — 12 (Boards 12, 18 D), 11.5 (Board 18 E). */
  noteSize?: 12 | 11.5
  /**
   * On a phone, the sheet as Board 16 D draws it: its top 150 from the top of
   * the screen whatever it holds, the body scrolling and the footer pinned.
   * Left out, a phone's sheet is as tall as what it holds.
   */
  tall?: boolean
  /** `VISIT_LAYER`, or `VISIT_LAYER_OVER` for a box opened from a box. */
  layer?: number
  /** `data-as-drawn` — the board panel this box is (`vp-12 A`). */
  asDrawn?: string
  children: ReactNode
}

/** A day, beside the page — Board 11 A. */
export function VisitPanel(props: VisitBoxProps) {
  return <VisitBox shape="panel" {...props} />
}

/** A sheet as tall as what it holds, at the right edge — Board 12, Board 18 D. */
export function VisitCard(props: VisitBoxProps) {
  return <VisitBox shape="card" {...props} />
}

/** A question in the middle of the page — Board 11 B, C; 17 F; 18 E, H; 7 B. */
export function VisitPopup(props: VisitBoxProps) {
  return <VisitBox shape="popup" {...props} />
}

function VisitBox({ phone: forced, ...props }: VisitBoxProps & { shape: Shape }) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  return phone ? <PhoneBox {...props} /> : <DesktopBox {...props} />
}

function CloseButton({ shape, onClose }: { shape: Shape | 'phone'; onClose: () => void }) {
  return (
    <button
      type="button"
      aria-label={COMMON.close}
      onClick={onClose}
      className={cn(
        'inline-flex shrink-0 items-center justify-center text-text-muted hover:text-text',
        shape === 'panel' && 'h-8 w-8 rounded-full hover:bg-hover',
        shape === 'popup' && 'h-[30px] w-[30px] rounded-full hover:bg-hover',
        shape === 'card' && 'h-[17px] w-[17px]',
        shape === 'phone' && 'h-8 w-8 rounded-full border border-line',
      )}
    >
      <VisitGlyph name="close" size={shape === 'popup' ? 16 : shape === 'phone' ? 15 : 17} stroke={2} />
    </button>
  )
}

/**
 * A popup's top, on a whole pixel. Centring with flex puts a box whose height
 * has a fraction in it (text at `line-height: normal` has one) on a fraction
 * of a pixel, and every line of text inside is then antialiased differently
 * from the board, which draws its popups at whole pixels.
 */
function useWholeTop(box: RefObject<HTMLDivElement>, on: boolean): number | null {
  const [top, setTop] = useState<number | null>(null)
  useLayoutEffect(() => {
    const node = box.current
    if (!on || !node) return
    const place = () => setTop(Math.max(16, Math.round((window.innerHeight - node.getBoundingClientRect().height) / 2)))
    place()
    const watch = new ResizeObserver(place)
    watch.observe(node)
    window.addEventListener('resize', place)
    return () => { watch.disconnect(); window.removeEventListener('resize', place) }
  }, [box, on])
  return top
}

function DesktopBox({
  shape, width, title, sub, mark, onClose, footer, note, noteSize = 12, layer = VISIT_LAYER, asDrawn, children,
}: Omit<VisitBoxProps, 'phone'> & { shape: Shape }) {
  const box = useRef<HTMLDivElement>(null)
  useDismiss(true, onClose, box, layer)
  const popup = shape === 'popup'
  const top = useWholeTop(box, popup)

  return createPortal((
    <div
      style={{ zIndex: layer }}
      data-overlay-layer={layer}
      className={cn(
        'fixed inset-0 flex',
        shape === 'panel' && 'justify-end bg-scrim/30',
        shape === 'card' && 'items-start justify-end bg-scrim/25',
        popup && 'items-start justify-center bg-scrim/25 px-4',
      )}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-as-drawn={asDrawn}
        style={popup ? { width, marginTop: top ?? 16, visibility: top === null ? 'hidden' : undefined } : { width }}
        className={cn(
          'flex max-w-full flex-col overflow-hidden bg-surface leading-[normal] tabular-nums text-text',
          shape === 'panel' && 'h-full border-l border-line shadow-[-14px_0_40px_rgba(20,20,10,0.14)]',
          shape === 'card'
            && 'max-h-[100dvh] rounded-record border border-line shadow-[0_10px_30px_rgba(20,20,10,0.08)]',
          popup
            && 'max-h-[calc(100dvh-32px)] rounded-record border border-line shadow-[0_14px_40px_rgba(20,20,10,0.14)]',
        )}
      >
        <header
          className={cn('flex shrink-0 items-center gap-3 border-b border-line-faint',
            popup ? 'px-4 py-3.5' : 'px-5 py-4')}
        >
          {mark}
          <span className="flex min-w-0 flex-col">
            <b className={cn('truncate', popup ? 'text-[14.5px]' : 'text-[16px]')}>{title}</b>
            {sub && <span className="truncate text-[12px] text-text-muted">{sub}</span>}
          </span>
          <span className="flex-1" />
          <CloseButton shape={shape} onClose={onClose} />
        </header>
        <div
          className={cn('min-h-0 overflow-y-auto',
            shape === 'panel' && 'flex-1',
            popup ? 'px-4 py-3.5' : 'px-5 py-[18px]')}
        >
          {children}
        </div>
        {footer && (
          <footer
            className={cn('flex shrink-0 items-center gap-2.5 border-t border-line bg-surface-alt',
              popup ? 'px-4 py-3' : 'px-5 py-3.5')}
          >
            {shape !== 'panel' && (note
              ? <p className="m-0 flex-1 leading-[1.4] text-text-muted" style={{ fontSize: noteSize }}>{note}</p>
              : <span className="flex-1" />)}
            {footer}
          </footer>
        )}
      </div>
    </div>
  ), document.body)
}

function PhoneBox({
  title, sub, onClose, footer, note, noteSize = 12, tall = false, layer = VISIT_LAYER, asDrawn, children,
}: Omit<VisitBoxProps, 'phone'> & { shape: Shape }) {
  const box = useRef<HTMLDivElement>(null)
  useDismiss(true, onClose, box, layer)
  return createPortal((
    <div
      style={{ zIndex: layer }}
      data-overlay-layer={layer}
      className="fixed inset-0 flex flex-col justify-end bg-scrim/[.38]"
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-as-drawn={asDrawn}
        className={cn('flex flex-col overflow-hidden rounded-t-sheet-wide bg-surface leading-[normal] tabular-nums',
          'text-text shadow-[0_-10px_30px_rgba(20,20,10,0.12)]',
          tall ? 'h-[calc(100dvh-150px)]' : 'max-h-[calc(100dvh-48px)]')}
      >
        <div className="flex shrink-0 justify-center pb-0.5 pt-2">
          <i className="block h-1 w-10 rounded-full bg-line-strong" />
        </div>
        <header className="flex shrink-0 items-center gap-3 border-b border-line-faint px-4 pb-3 pt-2.5">
          <span className="flex min-w-0 flex-col">
            <b className="text-[15.5px]">{title}</b>
            {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
          </span>
          <span className="flex-1" />
          <CloseButton shape="phone" onClose={onClose} />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
          {children}
          {note && <p className="m-0 mt-3.5 leading-[1.4] text-text-muted" style={{ fontSize: noteSize }}>{note}</p>}
        </div>
        {footer && (
          <footer className="flex shrink-0 items-center gap-2.5 border-t border-line px-4 pb-[18px] pt-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  ), document.body)
}
